import { ApolloError } from '@apollo/client';
import {
  HHButton,
  HHStack,
  HHTextField,
} from '@hinge-health/react-component-library';
import { Autocomplete } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { BaseSyntheticEvent, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  BillingType,
  ClientType,
  InsurerType,
  PartnershipType,
  PaymentType,
} from '../../../types';
import {
  capitalizeFirstLetterAndSpaceSnakeString,
  selectSorterFunction,
} from '../../../utils/bills-utils';

import { BillingTypeFilter } from '../../form/billing-type-filter';
import { ClientFilter } from '../../form/client-filter';
import { InsurerFilter } from '../../form/insurer-filter';
import { PartnershipFilter } from '../../form/partnership-filter';
import { AutocompleteSelectOption } from '../../grid-components/grid-types';
import { BILL_DATE_FORMAT } from '../bill-grid-components/bill-management-types';
import { UnbilledFilterModelType } from './unbilled-report';

export enum UnbilledFilterLabelText {
  CLIENTS = 'Clients',
  INSURERS = 'Insurers',
  PARTNERSHIPS = 'Partnerships',
  BILLING_TYPES = 'Billing Types',
  START_DATE = 'Lookback Start Date',
  END_DATE = 'Lookback End Date',
  PAYMENT_TYPES = 'Payment Types',
}

export interface UnbilledFiltersProps {
  clientsError: ApolloError | undefined;
  clientsData: ClientType[];
  insurersError: ApolloError | undefined;
  insurersData: InsurerType[];
  partnershipsError: ApolloError | undefined;
  partnershipsData: PartnershipType[];
  paymentTypesData: PaymentType[];
  paymentTypesError: ApolloError | undefined;
  billingTypesData: BillingType[];
  filterParams: UnbilledFilterModelType;
  setFilterParams: React.Dispatch<
    React.SetStateAction<UnbilledFilterModelType>
  >;
  default90DayLookbackStart: Date;
  today: Date;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  loading?: boolean;
}

interface RawUnbilleFilterValues {
  startDate: Date;
  endDate: Date;
  clients: ClientType[];
  insurers: InsurerType[];
  partnerships: PartnershipType[];
  billingTypes: AutocompleteSelectOption[];
  paymentTypes: PaymentType[];
}

export const UnbilledFilters = ({
  clientsError,
  clientsData,
  insurersError,
  insurersData,
  partnershipsError,
  partnershipsData,
  paymentTypesData,
  paymentTypesError,
  billingTypesData,
  filterParams,
  setFilterParams,
  default90DayLookbackStart,
  today,
  setPage,
  loading = false,
}: UnbilledFiltersProps): JSX.Element => {
  const defaultValues: RawUnbilleFilterValues = {
    endDate: dayjs(filterParams.endDate).toDate(),
    startDate: dayjs(filterParams.startDate).toDate(),
    clients: [],
    insurers: [],
    partnerships: [],
    billingTypes: [],
    paymentTypes: [],
  };

  const { handleSubmit, control, reset, formState } = useForm({
    mode: 'onChange',
    defaultValues,
    criteriaMode: 'all',
  });

  const { isValid, isDirty, errors } = formState;

  const sortedClients = [...clientsData].sort((a, b) =>
    selectSorterFunction(a.identifier, b.identifier),
  );

  const sortedInsurers = [...insurersData].sort((a, b) =>
    selectSorterFunction(a.name, b.name),
  );

  const sortedPartnerships = [...partnershipsData].sort((a, b) =>
    selectSorterFunction(a.name, b.name),
  );

  const parseFormValues = (data: RawUnbilleFilterValues): void => {
    const {
      startDate,
      endDate,
      clients,
      billingTypes,
      insurers,
      partnerships,
      paymentTypes,
    } = data;

    const formatted = {
      startDate: dayjs(startDate).format(BILL_DATE_FORMAT),
      endDate: dayjs(endDate).format(BILL_DATE_FORMAT),
      clientIds: clients.map(client => client.id),
      insurerIds: insurers.map(insurer => insurer.id),
      partnershipIds: partnerships.map(partnership => partnership.id),
      billingTypes: billingTypes.map(bt => bt.value),
      paymentTypes: paymentTypes.map(pt => pt.code),
    };

    setPage(1);
    setFilterParams(formatted);
  };

  const onFormSubmit = (e: BaseSyntheticEvent<object> | undefined): void => {
    handleSubmit(parseFormValues)(e);
  };

  const handleReset = (): void => {
    reset(defaultValues);
    setPage(1);
    setFilterParams({
      startDate: dayjs(default90DayLookbackStart).format(BILL_DATE_FORMAT),
      endDate: dayjs(today).format(BILL_DATE_FORMAT),
      clientIds: [],
      insurerIds: [],
      partnershipIds: [],
      billingTypes: [],
      paymentTypes: [],
    });
  };

  useEffect(() => {
    reset({
      startDate: dayjs(filterParams.startDate).toDate(),
      endDate: dayjs(filterParams.endDate).toDate(),
      clients: clientsData.filter(client =>
        filterParams.clientIds.includes(client.id),
      ),
      insurers: insurersData.filter(insurer =>
        filterParams.insurerIds.includes(insurer.id),
      ),
      partnerships: partnershipsData.filter(partnership =>
        filterParams.partnershipIds.includes(partnership.id),
      ),
      billingTypes: billingTypesData
        .filter(billingType =>
          filterParams.billingTypes.includes(billingType.value),
        )
        .map(billingType => ({
          value: billingType.value,
          label: capitalizeFirstLetterAndSpaceSnakeString(billingType.value),
        })),
      paymentTypes: paymentTypesData.filter(paymentType =>
        filterParams.paymentTypes.includes(paymentType.code),
      ),
    });
  }, [
    filterParams,
    reset,
    clientsData,
    insurersData,
    partnershipsData,
    paymentTypesData,
    billingTypesData,
  ]);

  return (
    <form>
      <HHStack
        direction="row"
        flexWrap="wrap"
        alignItems="flex-end"
        sx={{ gap: 4 }}
      >
        <ClientFilter
          control={control}
          clients={sortedClients}
          hasError={!!clientsError}
          errors={errors}
          variant="outlined"
          label={UnbilledFilterLabelText.CLIENTS}
          loading={loading}
        />
        <InsurerFilter
          control={control}
          insurers={sortedInsurers}
          hasError={!!insurersError}
          errors={errors}
          variant="outlined"
          label={UnbilledFilterLabelText.INSURERS}
          loading={loading}
        />
        <PartnershipFilter
          control={control}
          partnerships={sortedPartnerships}
          hasError={!!partnershipsError}
          errors={errors}
          variant="outlined"
          label={UnbilledFilterLabelText.PARTNERSHIPS}
          loading={loading}
        />
        <BillingTypeFilter
          control={control}
          errors={errors}
          billingTypes={billingTypesData}
          variant="outlined"
          label={UnbilledFilterLabelText.BILLING_TYPES}
          loading={loading}
        />
        <Controller // payment types
          control={control}
          name="paymentTypes"
          render={({ field: { onChange, value } }): JSX.Element => (
            <Autocomplete
              multiple
              id="payment-types-select"
              options={!paymentTypesError ? paymentTypesData : []}
              size="small"
              sx={{ minWidth: 230, maxWidth: 230 }}
              limitTags={1}
              blurOnSelect={true}
              onChange={(_, newValue): void => onChange(newValue || [])}
              value={value}
              disabled={loading}
              getOptionLabel={(option): string => option.code}
              isOptionEqualToValue={(option, value): boolean =>
                option.code === value.code
              }
              renderInput={(params): JSX.Element => (
                <HHTextField
                  {...params}
                  hhVariant="variant-bypass"
                  variant="outlined"
                  label={UnbilledFilterLabelText.PAYMENT_TYPES}
                  error={!!paymentTypesError}
                  helperText={
                    paymentTypesError ? 'payment types unavailable' : ''
                  }
                />
              )}
            />
          )}
        />
        <Controller //start date
          control={control}
          name="startDate"
          render={({ field: { onChange, value } }): JSX.Element => (
            <DatePicker
              label={UnbilledFilterLabelText.START_DATE}
              key={'startDate'}
              onChange={(newValue): void => {
                onChange(dayjs(newValue).toDate()); //parsing to date for validation
              }}
              value={value}
              disabled={loading}
              renderInput={(params): JSX.Element => (
                <HHTextField
                  {...params}
                  sx={{ minWidth: 230, maxWidth: 230 }}
                  error={params.error}
                  hhVariant="variant-bypass"
                  variant="outlined"
                  InputLabelProps={{ sx: { color: 'gray' } }}
                />
              )}
            />
          )}
        />
        <Controller //end date
          control={control}
          name="endDate"
          render={({ field: { onChange, value } }): JSX.Element => (
            <DatePicker
              label={UnbilledFilterLabelText.END_DATE}
              key={'endDate'}
              onChange={(newValue): void => {
                onChange(dayjs(newValue).toDate()); //parsing to date for validation
              }}
              value={value}
              disabled={loading}
              renderInput={(params): JSX.Element => (
                <HHTextField
                  {...params}
                  sx={{ minWidth: 230, maxWidth: 230 }}
                  error={params.error}
                  hhVariant="variant-bypass"
                  variant="outlined"
                  InputLabelProps={{ sx: { color: 'gray' } }}
                />
              )}
            />
          )}
        />
        <HHButton
          size="small"
          type="submit"
          hhVariant="contained"
          onClick={onFormSubmit}
          disabled={!isValid || !isDirty || loading}
        >
          Apply
        </HHButton>
        <HHButton
          size="small"
          hhVariant="text"
          onClick={handleReset}
          disabled={loading}
        >
          Reset
        </HHButton>
      </HHStack>
    </form>
  );
};
